/**
 * AutoLISP core value model and AST (P2 of docs/plans/2d/autocad-pgp-lisp-plan.md).
 *
 * Value kinds mirror AutoLISP: INT, REAL, STR, SYM, LIST (with dotted-pair
 * support), T, nil, USUBR/SUBR, ENAME, PICKSET and a FILE stub. Symbol names
 * are uppercased at lex time — AutoLISP symbols are case-insensitive.
 */

import type { LispNode } from './parser';

// ---------------------------------------------------------------------------
// Singleton atoms
// ---------------------------------------------------------------------------

/** AutoLISP nil — the only falsy value. */
export const NIL: { k: 'nil' } = { k: 'nil' };
/** AutoLISP T. */
export const T: { k: 't' } = { k: 't' };

export type LispValue =
    | typeof NIL
    | typeof T
    | { k: 'int'; v: number }
    | { k: 'real'; v: number }
    | { k: 'str'; v: string }
    | { k: 'sym'; name: string }
    | { k: 'list'; items: LispValue[]; dot?: LispValue | null }
    | { k: 'usubr'; name: string; params: string[]; body: LispNode[] }
    | { k: 'subr'; name: string; fn: SubrFn }
    | { k: 'ename'; id: string }
    | { k: 'pickset'; ids: string[] }
    | { k: 'file'; handle: string };

export type SubrFn = (
    args: LispValue[],
    rt: LispRuntimeApi
) => LispValue | Promise<LispValue>;

/**
 * The slice of the runtime primitive functions may use — declared here so
 * stdlib files never need a runtime import cycle with interpreter.ts.
 */
export interface LispRuntimeApi {
    /** Evaluate one node in an environment (defaults to the global env). */
    evalNode(node: LispNode, env?: Env): Promise<LispValue>;
    /** Evaluate a runtime value as code (the `eval` subr). */
    evalValue(v: LispValue): Promise<LispValue>;
    /** Apply a function value to evaluated arguments. */
    apply(fn: LispValue, args: LispValue[]): Promise<LispValue>;
    /** Command-transcript output (princ/prompt/...). */
    out(text: string): void;
    /** Global environment (dynamic-scope root). */
    readonly globalEnv: Env;
}

/**
 * Dynamic-scope environment chain. AutoLISP is dynamically scoped: function
 * calls bind parameters in a child of the GLOBAL environment, and free
 * symbols resolve through the caller chain at reference time. Keys are
 * normalized to uppercase — AutoLISP symbols are case-insensitive, and
 * subrs may register with any casing (`car`, `PRINC`, ...).
 */
export class Env {
    readonly vars = new Map<string, LispValue>();

    constructor(readonly parent: Env | null) {}

    lookup(name: string): LispValue {
        const key = name.toUpperCase();
        let env: Env | null = this;
        while (env) {
            const hit = env.vars.get(key);
            if (hit !== undefined) return hit;
            env = env.parent;
        }
        return NIL;
    }

    /** Assign to the nearest existing binding; false when unbound. */
    assign(name: string, value: LispValue): boolean {
        const key = name.toUpperCase();
        let env: Env | null = this;
        while (env) {
            if (env.vars.has(key)) {
                env.vars.set(key, value);
                return true;
            }
            env = env.parent;
        }
        return false;
    }

    define(name: string, value: LispValue): void {
        this.vars.set(name.toUpperCase(), value);
    }
}

// ---------------------------------------------------------------------------
// Constructors and predicates
// ---------------------------------------------------------------------------

export function mkInt(v: number): LispValue {
    return { k: 'int', v: Math.trunc(v) };
}

export function mkNumber(v: number): LispValue {
    return Number.isInteger(v) ? { k: 'int', v } : { k: 'real', v };
}

export function mkReal(v: number): LispValue {
    return { k: 'real', v };
}

export function mkStr(v: string): LispValue {
    return { k: 'str', v };
}

export function mkSym(name: string): LispValue {
    return { k: 'sym', name: name.toUpperCase() };
}

export function mkList(items: LispValue[], dot?: LispValue | null): LispValue {
    return { k: 'list', items, dot: dot ?? null };
}

export function mkEname(id: string): LispValue {
    return { k: 'ename', id };
}

export function mkPickset(ids: string[]): LispValue {
    return { k: 'pickset', ids };
}

export function isNil(v: LispValue): boolean {
    return v.k === 'nil';
}

/** AutoLISP truthiness: everything except nil is true. */
export function truthy(v: LispValue): boolean {
    return v.k !== 'nil';
}

export function isNumber(v: LispValue): boolean {
    return v.k === 'int' || v.k === 'real';
}

export function numVal(v: LispValue): number {
    if (v.k === 'int' || v.k === 'real') return v.v;
    return 0;
}

export function strVal(v: LispValue): string {
    return v.k === 'str' ? v.v : '';
}

export function symName(v: LispValue): string | null {
    return v.k === 'sym' ? v.name : null;
}

/** car — nil-safe. */
export function car(v: LispValue): LispValue {
    if (v.k !== 'list' || v.items.length === 0) return NIL;
    return v.items[0];
}

/** cdr — nil-safe; cdr of a one-element list is nil, of a dotted pair the dot. */
export function cdr(v: LispValue): LispValue {
    if (v.k !== 'list' || v.items.length === 0) return NIL;
    if (v.items.length === 1) return v.dot ?? NIL;
    return mkList(v.items.slice(1), v.dot);
}

/** AutoLISP list-ness: proper lists and dotted pairs count, nil does not. */
export function isListValue(v: LispValue): boolean {
    return v.k === 'list';
}

// ---------------------------------------------------------------------------
// Printing (prin1 style vs princ style)
// ---------------------------------------------------------------------------

function escapeString(s: string): string {
    return s
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\n/g, '\\n')
        .replace(/\t/g, '\\t')
        .replace(/\r/g, '\\r');
}

function formatNumber(v: number, isInt: boolean): string {
    if (isInt) return String(Math.trunc(v));
    const s = String(v);
    return /[-.eE]/.test(s) ? s : `${s}.0`;
}

/** Print a value the way AutoLISP's prin1/princ would. */
export function printValue(v: LispValue, princ = false): string {
    switch (v.k) {
        case 'nil':
            return 'nil';
        case 't':
            return 'T';
        case 'int':
            return formatNumber(v.v, true);
        case 'real':
            return formatNumber(v.v, false);
        case 'str':
            return princ ? v.v : `"${escapeString(v.v)}"`;
        case 'sym':
            return v.name;
        case 'list': {
            const inner = v.items.map((item) => printValue(item, princ));
            if (v.dot) inner.push('.', printValue(v.dot, princ));
            return `(${inner.join(' ')})`;
        }
        case 'usubr':
            return `#<USUBR @ ${v.name}>`;
        case 'subr':
            return `#<SUBR @ ${v.name}>`;
        case 'ename':
            return `<Entity name: ${v.id}>`;
        case 'pickset':
            return `<Selection set: ${v.ids.length}>`;
        case 'file':
            return `#<FILE ${v.handle}>`;
    }
}

/** Type names returned by the AutoLISP `type` function. */
export function typeSymbol(v: LispValue): LispValue {
    switch (v.k) {
        case 'nil':
            return NIL;
        case 't':
            return mkSym('SYM');
        case 'int':
            return mkSym('INT');
        case 'real':
            return mkSym('REAL');
        case 'str':
            return mkSym('STR');
        case 'sym':
            return mkSym('SYM');
        case 'list':
            return mkSym('LIST');
        case 'usubr':
            return mkSym('USUBR');
        case 'subr':
            return mkSym('SUBR');
        case 'ename':
            return mkSym('ENAME');
        case 'pickset':
            return mkSym('PICKSET');
        case 'file':
            return mkSym('FILE');
    }
}

/** Deep equality with AutoLISP's default 1e-8 real fuzz. */
export function valuesEqual(a: LispValue, b: LispValue, fuzz = 1e-8): boolean {
    if (a.k === 'nil' || b.k === 'nil') return a.k === b.k;
    if (a.k === 't' || b.k === 't') return a.k === b.k;
    if (isNumber(a) && isNumber(b)) return Math.abs(numVal(a) - numVal(b)) <= fuzz;
    if (a.k === 'str' && b.k === 'str') return a.v === b.v;
    if (a.k === 'sym' && b.k === 'sym') return a.name === b.name;
    if (a.k === 'ename' && b.k === 'ename') return a.id === b.id;
    if (a.k === 'list' && b.k === 'list') {
        if (a.items.length !== b.items.length) return false;
        for (let i = 0; i < a.items.length; i++) {
            if (!valuesEqual(a.items[i], b.items[i], fuzz)) return false;
        }
        if (!a.dot !== !b.dot) return false;
        if (a.dot && b.dot) return valuesEqual(a.dot, b.dot, fuzz);
        return true;
    }
    return a === b;
}
